import { useEffect, useState } from 'react';
import { PersonButton } from '@/features/people/PersonDrawer';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronDown, ClipboardList, FileText, Image as ImageIcon, Send, X } from 'lucide-react';
import {
  ApiError, formDefaults, formatFormValue, groupBySections, linkDay, validateFormValues,
  type FormField, type FormSubmissionView, type FormTemplate, type FormValues, type Message,
} from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useMembers } from '@/lib/hooks';
import { messageStore } from '@/lib/messageStore';
import { useUi } from '@/lib/store';
import { openAttachment } from '@/lib/upload';
import { DialogFooter, Avatar, Button, Menu, MenuItem, Modal, Spinner } from '@/components/ui';
import { FormFieldsEditor, FormSectionHeading } from './FormFields';
import { RelativeTime } from '@/components/time/RelativeTime';
import { Link } from 'react-router-dom';
import { useNavAccess } from '@/lib/navAccess';

export function StatusPill({ status, statuses, className }: { status: string | null; statuses: FormSubmissionView['statuses']; className?: string }) {
  if (!status) return null;
  const s = statuses.find(x => x.key === status);
  const color = s?.color ?? '#64748b';
  return (
    <span className={clsx('inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-slate-600 dark:text-slate-300', className)} data-testid="form-status">
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />{s?.label ?? status}
    </span>
  );
}

function Value({ f, form, m }: { f: FormField; form: FormSubmissionView; m: Message }) {
  const v = form.values[f.id];
  const toast = useUi(s => s.toast);
  if (v === undefined) return <span className="text-slate-500">-</span>;
  if (f.type === 'person') return (
    <span className="flex flex-wrap gap-2">{(v as string[]).map(id => <span key={id} className="flex items-center gap-1.5"><Avatar size={20} id={id} name={form.labels[id]} />{form.labels[id] ?? 'Unknown'}</span>)}</span>
  );
  if (f.type === 'file') return (
    <span className="flex flex-wrap gap-1.5">
      {(v as string[]).map(id => {
        const a = m.attachments?.find(x => x.id === id);
        const img = a?.content_type.startsWith('image/');
        return (
          <button key={id} onClick={() => openAttachment(id).catch(e => toast(errorMessage(e), 'error'))}
            className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs hover:border-accent-line dark:border-slate-700 dark:bg-slate-900">
            {img ? <ImageIcon className="h-3.5 w-3.5 text-accent-fg" /> : <FileText className="h-3.5 w-3.5 text-accent-fg" />}
            <span className="max-w-44 truncate">{form.labels[id] ?? a?.file_name ?? 'File'}</span>
          </button>
        );
      })}
    </span>
  );
  if (f.type === 'multi_select') return <span className="flex flex-wrap gap-1">{(v as string[]).map(x => <span key={x} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium dark:bg-slate-800">{x}</span>)}</span>;
  if (f.type === 'long_text') return <span className="whitespace-pre-wrap">{String(v)}</span>;
  return <span>{formatFormValue(f, v, form.labels)}</span>;
}

/** Structured card for a form post; managers can change the status inline. */
export function FormCard({ m, form, canSetStatus }: { m: Message; form: FormSubmissionView; canSetStatus?: boolean }) {
  const toast = useUi(s => s.toast);
  const { byId } = useMembers();
  const [menu, setMenu] = useState(false);
  const setStatus = async (key: string) => {
    setMenu(false);
    try { messageStore.applyUpdate(m.conversation_id, await api.setFormStatus(m.id, key)); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const answered = (f: FormField) => form.values[f.id] !== undefined && !(f.type === 'checkbox' && form.values[f.id] === false);
  // t725u: answers sit under their section headings; a section with no answers is left out.
  const groups = groupBySections(form.fields, form.sections ?? []).map(g => ({ ...g, items: g.items.filter(answered) })).filter(g => g.items.length);
  return (
    <div className={clsx('mt-1 max-w-2xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900', m.completed_at && 'opacity-80')} data-testid="form-card">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-3.5 py-2 dark:border-slate-800 dark:bg-slate-800/50">
        <span className="text-base leading-none">{form.emoji || <ClipboardList className="h-4 w-4 text-accent-fg" />}</span>
        <span className="text-sm font-semibold">{form.template_name}</span>
        {form.version < form.latest_version && <span className="text-[11px] text-slate-500" title="Submitted with an earlier version of this form">form v{form.version}</span>}
        <span className="ml-auto flex items-center gap-2">
          {form.status && form.statuses.length > 0 && (canSetStatus ? (
            <Menu open={menu} setOpen={setMenu} trigger={
              <button title="Change status" className="flex items-center gap-0.5 rounded px-1 hover:bg-slate-100 dark:hover:bg-slate-800"><StatusPill status={form.status} statuses={form.statuses} /><ChevronDown className="h-3 w-3 text-slate-500" /></button>
            }>
              {form.statuses.map(s => (
                <MenuItem key={s.key} onClick={() => void setStatus(s.key)} icon={<span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />}>{s.label}{s.key === form.status ? ' ✓' : ''}</MenuItem>
              ))}
            </Menu>
          ) : <StatusPill status={form.status} statuses={form.statuses} />)}
        </span>
      </div>
      <dl className="grid gap-x-6 gap-y-2.5 px-3.5 py-3 text-sm sm:grid-cols-2">
        {groups.map((g, k) => [
          g.section && <FormSectionHeading key={`s-${g.section.id}`} title={g.section.title} className={clsx('sm:col-span-2', k > 0 && 'pt-1')} />,
          ...g.items.map(f => (
            <div key={f.id} className={clsx('min-w-0', (f.type === 'long_text' || f.type === 'file') && 'sm:col-span-2')}>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{f.label}</dt>
              <dd className="mt-0.5 text-slate-800 dark:text-slate-100"><Value f={f} form={form} m={m} /></dd>
            </div>
          )),
        ])}
      </dl>
      <LinkedJourneyLine m={m} />
      {form.status_changed_at && form.status_changed_by && (
        <div className="border-t border-slate-100 px-3.5 py-1.5 text-[11px] text-slate-500 dark:border-slate-800">
          Status set by {form.status_changed_by && byId.get(form.status_changed_by) ? <PersonButton userId={form.status_changed_by} name={byId.get(form.status_changed_by)?.display_name} className="rounded hover:underline">{byId.get(form.status_changed_by)?.display_name}</PersonButton> : 'someone'} <RelativeTime iso={form.status_changed_at} />
        </div>
      )}
    </div>
  );
}

/**
 * t518u: a post linked to a journey shows the journey (muted, opens it for people who see fleet) and, once its stop
 * is done, "Completed". Mark complete / Reopen are in the message's ⋯ menu.
 */
export function LinkedJourneyLine({ m }: { m: Message }) {
  const { byId } = useMembers();
  const { can } = useNavAccess();
  const j = m.linked_journey;
  if (!j && !m.completed_at) return null;
  const label = j ? `Journey ${j.ref} · ${linkDay(j.planned_start)}${j.status === 'cancelled' ? ' · cancelled' : ''}` : null;
  const by = m.completed_by ? byId.get(m.completed_by) : undefined;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 border-t border-slate-100 px-3.5 py-1.5 text-[11px] text-slate-500 dark:border-slate-800" data-testid="form-linked-journey">
      {label && (can('/fleet/journeys') ? <Link to={`/fleet/journeys/${j!.id}`} className="hover:underline" data-testid="form-journey-link-open">{label}</Link> : <span>{label}</span>)}
      {m.completed_at && <span data-testid="form-post-completed">Completed{by ? <> by <PersonButton userId={by.user_id} name={by.display_name} className="rounded hover:underline">{by.display_name}</PersonButton></> : ''} <RelativeTime iso={m.completed_at} /></span>}
    </div>
  );
}

/** Uses the form view on the message, or fetches it when a realtime event arrived without one. */
export function FormPost({ m, canSetStatus }: { m: Message; canSetStatus?: boolean }) {
  const missing = !m.form && Boolean((m.metadata as { form?: unknown })?.form);
  const q = useQuery({ queryKey: ['form-post', m.id, m.edited_at], queryFn: () => api.formPost(m.id), enabled: missing });
  useEffect(() => { if (q.data?.form) messageStore.applyUpdate(m.conversation_id, { id: m.id, form: q.data.form }); }, [q.data, m.conversation_id, m.id]);
  const form = m.form ?? q.data?.form;
  if (!form) return <Spinner className="h-4 w-4" />;
  return <FormCard m={m} form={form} canSetStatus={canSetStatus} />;
}

const errorFields = (e: unknown): Record<string, string> | null =>
  e instanceof ApiError && e.body && typeof e.body === 'object' && 'errors' in e.body && !Array.isArray((e.body as { errors: unknown }).errors)
    ? (e.body as { errors: Record<string, string> }).errors : null;

/** The composer of a form channel: a collapsed bar that expands into the form. */
export function FormComposer({ conversationId, template, ownBuildingId, createsJourneys }: { conversationId: string; template: FormTemplate; ownBuildingId?: string | null; createsJourneys?: boolean }) {
  const defaults = () => formDefaults(template.fields, { own_building_id: ownBuildingId ?? null });
  const toast = useUi(s => s.toast);
  const key = `collab.formdraft.${conversationId}.${template.id}.v${template.version}`;
  const [open, setOpen] = useState(() => Boolean(sessionStorage.getItem(key)));
  const [values, setValues] = useState<FormValues>(() => { try { return JSON.parse(sessionStorage.getItem(key) ?? '') as FormValues; } catch { return defaults(); } });
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [clientId, setClientId] = useState(() => crypto.randomUUID());
  useEffect(() => { if (open) sessionStorage.setItem(key, JSON.stringify(values)); }, [key, open, values]);
  const reset = () => { setValues(defaults()); setErrors({}); setOpen(false); sessionStorage.removeItem(key); setClientId(crypto.randomUUID()); };
  const submit = async () => {
    const check = validateFormValues(template.fields, values);
    setErrors(check.errors);
    if (Object.keys(check.errors).length) { document.querySelector(`[data-field="${Object.keys(check.errors)[0]}"]`)?.scrollIntoView({ block: 'center' }); return; }
    setBusy(true);
    try {
      const m = await api.postForm(conversationId, { client_message_id: clientId, values: check.values });
      messageStore.ingest(conversationId, [m]);
      toast(`${template.name} posted`, 'success');
      reset();
    } catch (e) {
      const fe = errorFields(e);
      if (fe) setErrors(fe);
      toast(errorMessage(e), 'error');
    } finally { setBusy(false); }
  };
  const title = template.name.toLowerCase();
  if (!open) {
    return (
      <div className="px-4 pb-4">
        <div className="flex items-center gap-3 rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <span className="text-lg leading-none">{template.emoji || <ClipboardList className="h-5 w-5 text-accent-fg" />}</span>
          <div className="min-w-0 flex-1 text-sm text-slate-600 dark:text-slate-300">
            Posts in this channel use the <strong>{template.name}</strong> form. <span className="text-slate-500">Replies in threads are free text.{createsJourneys ? ' Each post creates a journey.' : ''}</span>
          </div>
          <Button onClick={() => setOpen(true)}><ClipboardList className="h-4 w-4" />New {title}</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="px-4 pb-4">
      <div className="flex max-h-[62vh] flex-col overflow-hidden rounded-xl border border-accent-line/25 bg-white shadow-lg ring-2 ring-accent-soft dark:bg-slate-900" aria-label={`New ${title}`} role="form">
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5 dark:border-slate-800">
          <span className="text-lg leading-none">{template.emoji}</span>
          <div className="font-semibold">New {title}</div>
          {template.description && <div className="hidden truncate text-xs text-slate-500 sm:block">· {template.description}</div>}
          <button className="ml-auto rounded p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" title="Close (draft is kept)" onClick={() => setOpen(false)}><X className="h-4 w-4" /></button>
        </div>
        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <FormFieldsEditor fields={template.fields} sections={template.sections} values={values} onChange={setValues} errors={errors} conversationId={conversationId}
            labels={labels} onLabel={(id, n) => setLabels(l => ({ ...l, [id]: n }))} />
        </div>
        <div className="flex items-center gap-2 border-t border-slate-100 px-4 py-2.5 dark:border-slate-800">
          <span className="text-xs text-slate-500"><span className="text-rose-500">*</span> required</span>
          <Button variant="ghost" className="ml-auto" onClick={reset}>Discard</Button>
          <Button loading={busy} onClick={() => void submit()}><Send className="h-4 w-4" />Post {title}</Button>
        </div>
      </div>
    </div>
  );
}

/** Author edits a form post (validated against the version it was posted with). */
export function FormEditModal({ m, onClose }: { m: Message; onClose: () => void }) {
  const toast = useUi(s => s.toast);
  const form = m.form!;
  const [values, setValues] = useState<FormValues>(form.values);
  const [labels, setLabels] = useState<Record<string, string>>(form.labels);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const check = validateFormValues(form.fields, values);
    setErrors(check.errors);
    if (Object.keys(check.errors).length) return;
    setBusy(true);
    try { messageStore.applyUpdate(m.conversation_id, await api.editFormPost(m.id, check.values)); toast('Updated', 'success'); onClose(); } catch (e) {
      const fe = errorFields(e); if (fe) setErrors(fe); toast(errorMessage(e), 'error');
    } finally { setBusy(false); }
  };
  return (
    <Modal open wide onClose={onClose} title={`Edit ${form.template_name.toLowerCase()}`}
      footer={<DialogFooter onCancel={onClose} onSave={() => void save()} saving={busy} saveLabel="Save changes" />}>
      {form.version < form.latest_version && <p className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:bg-slate-800">This post uses version {form.version} of the form, so it keeps those fields.</p>}
      <FormFieldsEditor fields={form.fields} sections={form.sections} values={values} onChange={setValues} errors={errors} conversationId={m.conversation_id} labels={labels} onLabel={(id, n) => setLabels(l => ({ ...l, [id]: n }))} />
    </Modal>
  );
}
